import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useAuth from './useAuth';
import * as XLSX from 'xlsx';
import Header from './Header.js';
import Sidebar from './Sidebar.js';
import Footer from './Footer.js';
import Pagination from './Pagination.js';
import './Manage_User.css';
import './Loader.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Loader = () => (
  <div className="loader-container1">
    <div className="spinner1"></div>
    <p>Loading...</p>
  </div>
);

const ManageZMConveyance = () => {
  const [EmpCode, setEmpCode] = useState('');
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [popupData, setPopupData] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  useAuth();

  // ✅ Fetch Conveyance Master Data
  useEffect(() => {
    const fetchConveyanceMaster = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          `https://travelapp.vdcapp.in/backend/api/manage_conveyance_master`,
          {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) throw new Error(`HTTP error! ${response.status}`);
        const result = await response.json();

        if (result.status && Array.isArray(result.data)) {
          setItems(result.data);
        } else {
          setError('No data found');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError('Failed to fetch conveyance data');
      } finally {
        setLoading(false);
      }
    };

    fetchConveyanceMaster();
  }, []);

  // ✅ Search and Sort logic
  useEffect(() => {
    const filtered = items.filter((item) => {
      const q = searchQuery.toLowerCase();
      return (
        (item.emp_code && item.emp_code.toLowerCase().includes(q)) ||
        (item.role && item.role.toLowerCase().includes(q)) ||
        (item.duration_type && item.duration_type.toLowerCase().includes(q)) ||
        (item.amount && item.amount.toString().includes(q)) ||
        (item.branches && item.branches.toLowerCase().includes(q))
      );
    });

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortOrder === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredItems(sorted);
    setCurrentPage(1);
  }, [searchQuery, items, sortColumn, sortOrder]);

  // ✅ Pagination setup
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const currentItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ✅ Event Handlers
  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setSortColumn(column);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  // ✅ Export to Excel
  const exportToExcel = () => {
    const excelData = filteredItems.map((item) => ({
      EmpCode: item.emp_code,
      Role: item.role,
      DurationType: item.duration_type,
      '1_15 Count': item['1_15_cnt'],
      '15_30 Count': item['15_30_cnt'],
      '1_30 Count': item['1_30_cnt'],
      Amount: item.amount,
      Branches: item.branches,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Conveyance Master');
    XLSX.writeFile(workbook, 'Conveyance_Master.xlsx');
  };

  // ✅ Fetch and show Punch Details in popup
  const handleCheckClick = async (empCode) => {
    try {
        const token = localStorage.getItem('authToken');
      const response = await fetch(
        `https://travelapp.vdcapp.in/backend/api/checkLoginForZMCMConveyance/${empCode}`,
          {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
          }
      );

      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
      const result = await response.json();

      if (result.status === 'success' && result.data && result.data.length > 0) {
        setPopupData(result.data[0]);
        setShowPopup(true);
      } else {
        setPopupData({ message: 'No punch data found' });
        setShowPopup(true);
      }
    } catch (error) {
      console.error('Error fetching punch details:', error);
      setPopupData({ message: 'Error fetching data' });
      setShowPopup(true);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData(null);
  };

  return (
    <div className="hold-transition sidebar-mini layout-fixed" style={{ fontSize: '12px' }}>
      <div className="wrapper">
        <Header />
        <Sidebar />
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h4 className="m-0" style={{ fontSize: '20px' }}>
                    ZM Conveyance Master
                  </h4>
                </div>
                <div className="col-sm-6">
                  <ol className="breadcrumb float-sm-right">
                    <li className="breadcrumb-item">
                      <Link to="/dashboard">Home</Link>
                    </li>
                    <li className="breadcrumb-item active">ZM Conveyance Master</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <section className="col-lg-12 connectedSortable">
                  <div className="card">
                    <div className="card-header">
                      <span style={{ float: 'right' }}>
                        <Link to="/add_localzmconveyance">
                          <button type="button" className="btn btn-success">Add New</button>
                        </Link>
                        <button type="button" className="btn btn-secondary ml-2" onClick={exportToExcel}>
                          Export to Excel
                        </button>
                      </span>
                    </div>
                    <div className="card-body">
                      {loading ? (
                        <Loader />
                      ) : (
                        <>
                          <div className="dataTables_filter" style={{ float: 'right' }}>
                            <label>
                              <input
                                type="search"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="form-control form-control-sm"
                                placeholder="Search"
                              />
                            </label>
                          </div>

                          <div className="form-group">
                            <select
                              className="form-control custom-select-sm"
                              value={itemsPerPage}
                              onChange={handleItemsPerPageChange}
                            >
                              <option value={10}>10</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>

                          <div className="table-responsive">
                            <table className="table table-bordered table-striped" style={{ fontSize: '13px' }}>
                              <thead>
                                <tr>
                                  <th>ID</th>
                                  <th>Emp Code</th>
                                  <th>Role</th>
                                  <th>Duration Type</th>
                                  <th>1-15 Count</th>
                                  <th>15-30 Count</th>
                                  <th>1-30 Count</th>
                                  <th>Amount</th>
                                  <th>Branches</th>
                                  <th>Branch Names</th>
                                  <th>Action</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {currentItems.length > 0 ? (
                                  currentItems.map((item, index) => (
                                    <tr key={index}>
                                      <td>{item.id}</td>
                                      <td>{item.emp_code}</td>
                                      <td>{item.role}</td>
                                      <td>{item.duration_type}</td>
                                      <td>{item['1_15_cnt']}</td>
                                      <td>{item['15_30_cnt']}</td>
                                      <td>{item['1_30_cnt']}</td>
                                      <td>{item.amount}</td>
                                      <td>{item.branches}</td>
                                      <td>{item.branch_names}</td>
                                      <td>
                                        <button
                                          className="btn btn-info btn-sm"
                                          onClick={() => handleCheckClick(item.emp_code)}
                                        >
                                          Check
                                        </button>
                                      </td>
                                      <td>
                                        <button
                                          className="btn btn-info btn-sm"
                                          onClick={() => handleCheckClick(item.emp_code)}
                                        >
                                          Edit
                                        </button>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="11" style={{ textAlign: 'center' }}>
                                      No data available
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                  />

                  <div className="mb-3">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                    {Math.min(currentPage * itemsPerPage, filteredItems.length)} of{' '}
                    {filteredItems.length} entries
                  </div>
                </section>
              </div>
            </div>
          </section>
        </div>

        <Footer />
      </div>

      {/* ✅ Popup Modal */}
      {showPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '20px',
              width: '400px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            <h5 className="text-center mb-3">Punch Details</h5>
            {popupData && popupData.DeviceFName ? (
              <>
                <p><strong>User ID:</strong> {popupData.UserId}</p>
                <p><strong>Device ID:</strong> {popupData.DeviceId}</p>
                <p><strong>Device Name:</strong> {popupData.DeviceFName}</p>
                <p><strong>Log Date:</strong> {popupData.LogDate}</p>
              </>
            ) : (
              <p>{popupData?.message || 'No punch data available'}</p>
            )}
            <div className="text-center mt-3">
              <button className="btn btn-danger btn-sm" onClick={closePopup}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageZMConveyance;
